/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import Swal from "sweetalert2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Alert from "@mui/material/Alert";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import InputAdornment from "@mui/material/InputAdornment";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import TableHeader from "components/Table/TableHeader";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDDatePicker from "components/MDDatePicker";
import MDDateRange from "components/MDDateRange";
import MDTextarea from "components/MDTextarea";
import TabPanel from "components/TabPanel/TabPanel";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Navbar from "examples/Navbars/NavBar";
import Footer from "examples/Footer";
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";
import AdjustStockStatus from "./AdjustStockStatus";
import AdjustStockPrint from "./AdjustStockPrint";
import AdjustStockList from "./AdjustStockList";

const initAttr = {
  id: "",
  code: "",
  branch_id: "",
  remark: "",
  status: "edit",
  created_at: "",
  updated_at: "",
  date_record: Func.dateNow(),
};

const initFind = {
  search: "",
  order_by: "tb.id",
  order_sort: "desc",
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: "ALL",
  period: "ALL",
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const stepperName = {
  edit: {
    name: "แก้ไขข้อมูล",
    color: "warning",
    icon: "border_color",
  },
  quotation: {
    name: "เสนอราคา",
    color: "info",
    icon: "task_alt",
  },
  approve: {
    name: "ลูกค้าสั่งซื้อ",
    color: "success",
    icon: "task_alt",
  },
  reject: {
    name: "ยกเลิก",
    color: "error",
    icon: "cancel",
  },
};

const cboStatus = [
  { id: "ALL", name: "สถานะทั้งหมด" },
  { id: "edit", name: stepperName.edit.name },
  { id: "quotation", name: stepperName.quotation.name },
  { id: "approve", name: stepperName.approve.name },
  { id: "reject", name: stepperName.reject.name },
];

const initStepper = {};

function AdjustStock() {
  const mod = "adjust_stock";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [stepper, setStepper] = useState(initStepper);
  const [tab, setTab] = useState(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: "tb.status", label: t(`attr.enable`), align: "center" },
    { id: "tb.code", label: t(`attr.code`), align: "center" },
    { id: "tb.user_id", label: "ผู้ทำรายการ", align: "left" },
    { id: "tb.remark", label: "หมายเหตุ", align: "left" },
    { id: "tb.branch_id", label: "สาขา", align: "left" },
    { id: "tb.date_record", label: "วันปรับปรุง", align: "center" },
    { id: "print", label: "", align: "center", sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData("POST", `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === "ok") {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setTab(0);

    FetchData("GET", `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(result.data);
        setStepper(result.stepper);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.date_record, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData("PUT", `/${mod}`, app.token, {
      date_record: attr.date_record,
      remark: attr.remark,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), "success");
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.date_record, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);

    console.log(attr);

    FetchData("PATCH", `/${mod}/${attr.id}`, app.token, {
      date_record: attr.date_record,
      remark: attr.remark,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        window.location.reload();
        showNotify(t(`alert.editcomplete`), "success");
      } else {
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: "warning",
      showDenyButton: true,
      confirmButtonColor: "#3085d6",
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: "#dc3545",
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData("OPTIONS", `/${mod}/approve`, app.token, {
          id: id,
          status: status,
        }).then((result) => {
          setLoading(false);

          if (result.status === "ok") {
            window.location.reload();
            showNotify(t(`alert.editcomplete`), "success");
          } else {
            showNotify(t(`alert.saveerror`), "error");
          }
        });
      }
    });
  };

  const del = () => {
    setLoading(true);
    FetchData("DELETE", `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view(find);
        showNotify(t(`alert.deletecomplete`), "success");
      } else {
        showNotify(t(`alert.deleteerror`), "error");
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? "desc" : "asc" });
  };

  const init = () => {
    FetchData("GET", `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(initAttr);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant="text"
          color="info"
          size="small"
          sx={{ fontSize: "1rem", whiteSpace: "nowrap" }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnPrint = (item) => {
    return <AdjustStockPrint parent={item} />;
  };

  const statusDiaplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: "14px",
          padding: "0 10px",
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: "nowrap",
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <MDBox
                    noValidate
                    component="form"
                    role="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      view({ ...find, period: "ALL", page: 1 });
                    }}
                  >
                    <MDInput
                      name="searchform"
                      fullWidth
                      value={find.search}
                      type="search"
                      onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">{t("element.search")} :</InputAdornment>
                        ),
                        autoComplete: "off",
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <MDInput
                    fullWidth
                    value={find.status}
                    defaultValue="ALL"
                    select
                    onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
                  >
                    {cboStatus.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                </Grid>

                {find.period === "ALL" ? (
                  <Grid item xs={12} sm={12} md={6} lg={2}>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      sx={{ p: 1.5 }}
                      onClick={() => view({ ...find, period: "DATE", page: 1 })}
                    >
                      <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
                    </MDButton>
                  </Grid>
                ) : null}
                {find.period === "ALL" ? null : (
                  <Grid item xs={12} sm={12} md={12} lg={7}>
                    <MDDateRange
                      dateStart={find.date_start}
                      dateStop={find.date_stop}
                      callBack={setDateRange}
                      closeDate={() => view({ ...find, period: "ALL", page: 1 })}
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant="contained"
              color="dark"
              size="large"
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center" width={160}>
                        {statusDiaplay(row)}
                      </TableCell>
                      <TableCell align="center">{btnOpen(row)}</TableCell>
                      <TableCell align="left">{row.user_name}</TableCell>
                      <TableCell align="left">{row.remark}</TableCell>
                      <TableCell align="left">{row.branch_name}</TableCell>
                      <TableCell align="center">{Func.dateDisplay(row.date_record)}</TableCell>
                      <TableCell align="center">{btnPrint(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <MDButton variant={show ? "contained" : "outlined"} color="dark" onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant="outlined"
                  color="secondary"
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    const readOnly = attr.status !== "edit";

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_record: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_record)}
            input={{
              label: "วันที่ปรับปรุง",
              variant: "standard",
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDTextarea
            label={`${t(`attr.remark`)} *`}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant="standard"
            inputProps={{
              maxLength: 200,
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth="sm" keepMounted aria-describedby="dialog-add">
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label="close"
            onClick={() => setModalAdd(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minWidth: 500 }}>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth="sm"
        keepMounted
        aria-describedby="dialog-delete"
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label="close"
            onClick={() => setModalDel(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="gradient" color="error" onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="error" onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case "edit":
          return key === "quotation";
        case "quotation":
          return key === "approve";
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step>
          <StepLabel StepIconComponent={AdjustStockStatus}>
            {action(key) ? (
              <MDButton variant="gradient" color="info" size="small" onClick={() => approve(key)}>
                <Icon>task_alt</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant="subtitle2">{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant="contained"
                  color={stepperName[key].color}
                  size="md"
                  container
                />
                <MDTypography variant="subtitle2">{stepper[key].emp_name}</MDTypography>
                <MDTypography variant="subtitle2">
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant="h6">{stepperName[key].name}</MDTypography>
                <MDTypography variant="subtitle2">{stepper[key].emp_name}</MDTypography>
                <MDTypography variant="subtitle2">
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === "edit") return 1;
      if (attr.status === "quotation") return 2;
      if (attr.status === "approve") return 3;
      if (attr.status === "reject") return 9;

      return 0;
    };

    return (
      <MDBox sx={{ width: "100%" }} mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel("edit")}
          {stepLevel("quotation")}
          {stepLevel("approve")}
          {stepLevel("reject")}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant="h5" mb={3}>
          #{attr.code}
        </MDTypography>
        {renderInfo()}

        {confirm ? (
          <Grid container mt={3}>
            <Grid item xs={6}>
              <MDButton variant="outlined" color="error" onClick={() => setConfirm(false)}>
                <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display="flex" justifyContent="flex-end">
              <MDButton variant="gradient" color="success" onClick={edit}>
                <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : attr.status === "edit" ? (
          <Grid container mt={3}>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
              <MDButton variant="outlined" color="success" onClick={editConfirm}>
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : null}
      </Paper>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                <NavLink to={`/${mod}`}>
                  <MDButton variant="outlined" color="warning">
                    <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
                  </MDButton>
                </NavLink>
              </MDBox>
            </Card>
          </Grid>
          {renderStepper()}

          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: "100%" }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="tabs-addedit">
                    <Tab label="ข้อมูล" />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container mb={3} spacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={3}>
                      {renderInfoEdit()}
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={9}>
                      <MDBox className="card">
                        <AdjustStockList />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="center" gap={1}>
                        <MDButton
                          variant="gradient"
                          color="error"
                          onClick={() => {
                            approve("reject");
                          }}
                        >
                          <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                        </MDButton>
                        <AdjustStockPrint parent={attr} size="large" />
                      </MDBox>
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.account`), t(`menu.adjust_stock`)]} />
      {renderPage()}
      {renderAdd()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default AdjustStock;
